import { useCallback, useEffect, useMemo, useState } from 'react';

import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';

import { isNudgeRecommendationSurface } from '../../common/utils/is-nudge-recommendation-surface';
import { fetchNudgeRecommendations } from './fetchNudgeRecommendations';

type useFetchNudgeRecommendationsReturnType = {
	error?: Error;
	loading: boolean;
	recommendations: NudgeRecommendationDetails[] | undefined;
};

type PreviousRequestCache = {
	pageUrl?: string;
};

export const useFetchNudgeRecommendations = (
	triggerId: string,
	shouldTrigger: boolean,
	pageUrl: string,
	product: string,
	isLiveDoc = false,
	isLiveViewMode = false,
): useFetchNudgeRecommendationsReturnType => {
	const [error, setError] = useState<Error | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [recommendations, setRecommendations] = useState<NudgeRecommendationDetails[] | undefined>(
		undefined,
	);
	const [previousRequestCache, setPreviousRequestCache] = useState<PreviousRequestCache>({});

	const makeNudgeRecommendationsRequest = useCallback(async () => {
		const shouldSkipDuplicatePageRequest = pageUrl === previousRequestCache.pageUrl;

		if (!isNudgeRecommendationSurface({ product, pageUrl, isLiveDoc, isLiveViewMode })) {
			setLoading(false);
			return;
		}

		if (
			!product || // invalid request parameter
			!shouldTrigger || // unsupported, no nudges to recommend
			shouldSkipDuplicatePageRequest // prevent duplicate page requests
		) {
			setLoading(false);
			return;
		}

		// Begin new request under valid conditions
		if (!loading && product && shouldTrigger && !shouldSkipDuplicatePageRequest) {
			setLoading(true);
			setError(undefined);
			setRecommendations(undefined);
			return;
		}

		try {
			const response = await fetchNudgeRecommendations({ triggerId, pageUrl, product });
			setRecommendations(
				response?.triggers.flatMap((trigger) =>
					trigger.recommendations.map((recommendation) => recommendation.recommendation_details),
				),
			);
			setError(undefined);
		} catch (error: unknown) {
			setRecommendations(undefined);
			setError(error as Error);
		} finally {
			setPreviousRequestCache({
				pageUrl,
			});
			setLoading(false);
		}
	}, [
		loading,
		pageUrl,
		previousRequestCache,
		product,
		shouldTrigger,
		triggerId,
		isLiveDoc,
		isLiveViewMode,
	]);

	useEffect(() => {
		void makeNudgeRecommendationsRequest();
	}, [loading, makeNudgeRecommendationsRequest]);

	return useMemo(
		() => ({
			recommendations: loading ? undefined : recommendations,
			loading,
			error,
		}),
		[loading, error, recommendations],
	);
};
